<!--
  -  Copyright (c) Qwoach LLC 2023. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 1/1/2023
  -->

<template>
	<div class="qw-uploader-root relative" :style="computedCssVars">
		<component
			:is="currentViewComponent"
			v-bind="currentViewProps"
			@clicked="onEmptyStateClicked"
			@mouseEntered="onEmptyStateMouseEnter"
				@mouseLeft="resetSourcePickerUi"
				@sourceSelected="onSourceSelected"
				@previewActionClicked="handlePreviewActionClicked"/>
		<LocalFilePicker
			@fileSelected="onFileSelected"
			@filesSelected="onFilesSelected"
			class="absolute"
			ref="localInput"
			:sizeLimit="sizeLimit"
			:filter="imageFilter"
			:filterNames="['image']"
			:loadFileOnSelect="loadFileOnSelect"
			:multiple="allowMultipleComputed"/>

	</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import LocalFilePicker from "@/core/components/File/LocalFilePicker.vue";
import {FileUploadStatus} from "@/core/components/File/FileUploadProgress/FileUploadStatus";
import QwoachFile from "@shared/Drive/QwoachFile";
import {FileTypesEnum} from "@/core/components/File/FileTypesEnum";
import {deleteConfirmDialog, imageDialog} from "@/core/components/Dialogs/dialog";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import {QwImageUploaderModeEnum} from "@/core/components/Controls/QwImageUploader/QwImageUploaderModeEnum";
import QwImageUploaderEmptyView from "@/core/components/Controls/QwImageUploader/QwImageUploaderEmptyView.vue";
import QwImageUploaderPreviewView from "@/core/components/Controls/QwImageUploader/QwImageUploaderPreviewView.vue";
import QwImageUploaderUploadingView from "@/core/components/Controls/QwImageUploader/QwImageUploaderUploadingView.vue";

const SOURCE_PICKER_HOVER_DELAY = 50 //we need a dealy to workaround a bug on mobile where there is no hover by clicking on picker generates extra "mouse enter" event which immediately shows the picer options and then triggers click as if user clicked a picker right away.
const PREVIEW_ACTION_ID_PREVIEW = "preview"
const PREVIEW_ACTION_ID_REPLACE = "replace"
const PREVIEW_ACTION_ID_DELETE = "delete"
const DEFAULT_PREVIEW_ACTIONS_ARRAY = [
	{id: PREVIEW_ACTION_ID_PREVIEW, icon: "icon-zoom-in", tooltip: "Preview image"},
	{id: PREVIEW_ACTION_ID_REPLACE, icon: "icon-arrows-cw", tooltip: "Replace image", tooltipPosition: "bottom"},
	{id: PREVIEW_ACTION_ID_DELETE, icon: "icon-trash", tooltip: "Delete image", tooltipPosition: "bottom"},
]

export default {
	name: "QwImageUploader",
	components: {QwImageUploaderUploadingView, QwImageUploaderPreviewView, QwImageUploaderEmptyView, LocalFilePicker},
	extends: BaseComponent,
	emits: ["uploaded", "deleted", "updated", "sourceSelected", "fileSelected", "filesSelected", "previewActionClicked"],

	props: {
		sizePx: {Number, default: 148},
		qwoachFile: QwoachFile,
		pickerSources: {type: Array, default: () => []},
		filter: {Array, default: () => []},
		filterNames: {Array, default: () => []},
		sizeLimit: Number,
		loadFileOnSelect: {type: Boolean, default: true},
		parentFolderId: String,
		shape: String,
		previewWidth: {Number, default: 200},
		previewActions: {type: Array, default: null},
		externalPreviewActionIds: {type: Array, default: () => []},
		autoUploadOnSelect: {type: Boolean, default: true},
		isLoading: Boolean,
		loadingText: String,
		allowMultiple: Boolean,
	},

	data() {
		return {
			fileUploadStatus: null,
			uploadProgress: 0,
			qwoachFileData: null,
			localFile: {},
			fileManager: null,
			isUpdatingFile: false,
			isDeletingFile: false,
			prevFile: null,
			showSourcePickerHoverTimeoutId: null,
			sourcePickerTargetFile: null,
			uiMode: QwImageUploaderModeEnum.EMPTY,
		}
	},

	watch: {
		qwoachFile() {
			this.syncQwoachFileData()
		}
	},

	created() {
		this.setup()
	},

	methods: {

		clear(){
			this.qwoachFileData = null
			this.fileUploadStatus = null
			this.uploadProgress = 0
			this.localFile = {}
			this.clearShowSourcePickerHoverTimeout()
			this.syncDefaultViewMode()
		},

		setup() {
			this.fileManager = this.$fileUploader.fileManager
			this.syncQwoachFileData()

			// setTimeout(() => {
			// 	this.$refs.imageView.setLoadingState(true)
			// }, 1000)
		},


		syncQwoachFileData() {
			this.qwoachFileData = this.qwoachFile
			if (this.qwoachFileData) {
				this.fileUploadStatus = FileUploadStatus.COMPLETED
			}
			this.syncDefaultViewMode()
		},

		async handleRemove(qwoachFile) {
			qwoachFile = qwoachFile || this.qwoachFileData
			let res = await deleteConfirmDialog({itemName: "image"})
			if (res) {
				await this.removeFile(qwoachFile)
			}
		},

		async removeFile(qwoachFile) {
			this.isDeletingFile = true
			try {
				let isDeleted = await this.deleteFile(qwoachFile)
				if(isDeleted){
					this.qwoachFileData = null
					this.fileUploadStatus = null
					this.uploadProgress = 0
					this.syncDefaultViewMode()
					this.$emit("deleted", qwoachFile)
				}
			} finally {
				this.isDeletingFile = false
			}
		},

		async deleteFile(qwoachFile) {
			return await this.handleAsync(() => this.fileManager.deleteForever(this.ownerProfile.id, [qwoachFile]), {default: true, defaultFail: false})
		},

		async handleUpdate(qwoachFile) {
			if(this.hasPickerSources){
				this.sourcePickerTargetFile = qwoachFile || this.qwoachFileData
				this.uiMode = QwImageUploaderModeEnum.SOURCE_PICKER
			} else {
				this.startDeviceUpdate(qwoachFile)
			}
		},

		async handlePreview(qwoachFile){
			await imageDialog({
				src: qwoachFile.url
			})
		},

		async handlePreviewActionClicked(event = {}) {
			let actionId = event.action?.id || null
			if(this.isExternalPreviewAction(actionId)){
				this.$emit("previewActionClicked", event)
			}
			if(!this.isExternalPreviewAction(actionId)){
				await this.handleInternalPreviewAction(actionId, event.qwoachFile)
			}
		},

		async handleInternalPreviewAction(actionId, qwoachFile) {
			if(actionId === PREVIEW_ACTION_ID_PREVIEW){
				await this.handlePreview(qwoachFile)
			}
			if(actionId === PREVIEW_ACTION_ID_REPLACE){
				await this.handleUpdate(qwoachFile)
			}
			if(actionId === PREVIEW_ACTION_ID_DELETE){
				await this.handleRemove(qwoachFile)
			}
		},

		isExternalPreviewAction(actionId) {
			return (this.externalPreviewActionIds || []).includes(actionId)
		},

		pickFile() {
			this.$refs.localInput.pickFile()
		},

		onEmptyStateClicked() {
			if(this.isLoadingComputed){
				return
			}
			if(this.hasPickerSources){
				this.clearShowSourcePickerHoverTimeout()
				this.setUiMode(QwImageUploaderModeEnum.SOURCE_PICKER, "onEmptyStateClicked")
			} else {
				this.pickFile()
			}
		},

		onEmptyStateMouseEnter() {
			if(this.hasPickerSources && !this.isLoadingComputed && this.uiMode === QwImageUploaderModeEnum.EMPTY){
				this.scheduleShowSourcePickerFromHover()
			}
		},

		resetSourcePickerUi() {
			this.clearShowSourcePickerHoverTimeout()
			this.syncDefaultViewMode()
			this.sourcePickerTargetFile = null
		},

		onSourceSelected(sourceItem) {
			if(sourceItem?.key === "device"){
				this.startDeviceUpdate(this.sourcePickerTargetFile)
			} else if(sourceItem?.key){
				this.$emit("sourceSelected", {
					sourceKey: sourceItem.key,
					qwoachFile: this.sourcePickerTargetFile || this.qwoachFileData || null,
				})
				this.resetSourcePickerUi()
			}
		},

		startDeviceUpdate(qwoachFile) {
			if(qwoachFile){
				this.isUpdatingFile = true
				this.prevFile = qwoachFile
			}
			this.resetSourcePickerUi()
			this.pickFile()
		},

		scheduleShowSourcePickerFromHover() {
			this.clearShowSourcePickerHoverTimeout()
			this.showSourcePickerHoverTimeoutId = setTimeout(() => {
				this.showSourcePickerHoverTimeoutId = null
				if(this.hasPickerSources && this.uiMode === QwImageUploaderModeEnum.EMPTY){
					this.setUiMode(QwImageUploaderModeEnum.SOURCE_PICKER, "hoverDelay")
				}
			}, SOURCE_PICKER_HOVER_DELAY)
		},

		clearShowSourcePickerHoverTimeout() {
			if(this.showSourcePickerHoverTimeoutId){
				clearTimeout(this.showSourcePickerHoverTimeoutId)
				this.showSourcePickerHoverTimeoutId = null
			}
		},

		setUiMode(nextMode, sourceEvent = null) {
			this.uiMode = nextMode
		},

		async onFileSelected(qwoachFile, fileContent) {
			console.log("onFileSelected: ", qwoachFile)
			if(!this.autoUploadOnSelect){
				this.$emit("fileSelected", {
					qwoachFile,
					fileContent,
					previousFile: this.prevFile,
					isUpdatingFile: this.isUpdatingFile,
				})
				this.isUpdatingFile = false
				this.prevFile = null
				this.syncDefaultViewMode()
				return
			}

			this.fileUploadStatus = FileUploadStatus.IN_PROGRESS
			this.uploadProgress = null
			this.uiMode = QwImageUploaderModeEnum.UPLOADING

			if(this.parentFolderId){
				qwoachFile.parentId = this.parentFolderId
			}
			this.qwoachFileData = qwoachFile
			this.localFile = {url: fileContent, ...qwoachFile._file}

			await this.fileManager.uploadFiles([qwoachFile], async (qwoachFile) => {
				this.fileUploadStatus = qwoachFile._uploadInfo.status
				this.uploadProgress = qwoachFile._uploadInfo.progress
				console.log("this.uploadProgress = ", this.uploadProgress, this.fileUploadStatus, ", this.isStatusUploaded = ", this.isStatusUploaded)
				if (this.isStatusUploaded) {
					let qwFile = classToClass(this.qwoachFileData, {excludePrefixes: ["_"]})
					if (this.isUpdatingFile) {
						await this.deleteFile(this.prevFile)
						this.isUpdatingFile = false
						this.$emit("updated", qwFile)
					} else {
						this.$emit("uploaded", qwFile)
					}
					this.syncDefaultViewMode()
				}
			}, {previewWidth: this.previewWidth})


		},

		async onFilesSelected(fileDataArray = []) {
			if(!this.autoUploadOnSelect || this.allowMultipleComputed){
				this.$emit("filesSelected", fileDataArray)
			} else if(fileDataArray.length){
				await this.onFileSelected(fileDataArray[0].qwoachFile, fileDataArray[0].fileContent)
			}
		},

		syncDefaultViewMode() {
			if(this.fileUploadStatus === FileUploadStatus.IN_PROGRESS){
				this.setUiMode(QwImageUploaderModeEnum.UPLOADING, "syncDefaultViewMode")
			} else if(this.qwoachFileData){
				this.setUiMode(QwImageUploaderModeEnum.PREVIEW, "syncDefaultViewMode")
			} else {
				this.setUiMode(QwImageUploaderModeEnum.EMPTY, "syncDefaultViewMode")
			}
		},
	},

	computed: {
		computedCssVars() {
			return {
				"--uploader-size": `${this.sizePx}px`
			}
		},

		thumbnailUrlComputed() {
			let thumbnailUrl = this.qwoachFileData?.thumbnailFile?.url || this.qwoachFileData?.url
			return this.localFile.url || thumbnailUrl
		},

		hasPickerSources() {
			return (this.pickerSources || []).length > 0
		},

		allowMultipleComputed() {
			return this.allowMultiple && !this.qwoachFileData && !this.isUpdatingFile
		},

		currentViewComponent() {
			let componentMap = {
				[QwImageUploaderModeEnum.EMPTY]: QwImageUploaderEmptyView,
				[QwImageUploaderModeEnum.SOURCE_PICKER]: this.qwoachFileData ? QwImageUploaderPreviewView : QwImageUploaderEmptyView,
				[QwImageUploaderModeEnum.UPLOADING]: QwImageUploaderUploadingView,
				[QwImageUploaderModeEnum.PREVIEW]: QwImageUploaderPreviewView,
			}
			return componentMap[this.uiMode]
		},

		currentViewProps() {
			let viewProps = {
				isCircleComputed: this.isCircleComputed,
				pickerSources: this.pickerSources,
				previewActions: this.previewActionsComputed,
				isLoading: this.isLoadingComputed,
				loadingText: this.loadingTextComputed,
			}
			let viewPropsByMode = {
				[QwImageUploaderModeEnum.EMPTY]: {
					...viewProps,
					showSourcePicker: false,
				},
				[QwImageUploaderModeEnum.SOURCE_PICKER]: {
					...viewProps,
					showSourcePicker: !this.isLoadingComputed,
					thumbnailUrlComputed: this.thumbnailUrlComputed,
					qwoachFileData: this.qwoachFileData,
				},
				[QwImageUploaderModeEnum.UPLOADING]: {
					...viewProps,
					uploadProgress: this.uploadProgress,
				},
				[QwImageUploaderModeEnum.PREVIEW]: {
					...viewProps,
					showSourcePicker: false,
					thumbnailUrlComputed: this.thumbnailUrlComputed,
					qwoachFileData: this.qwoachFileData,
				},
			}
			return viewPropsByMode[this.uiMode]
		},

		isLoadingComputed() {
			return this.isLoading || this.isDeletingFile
		},

		loadingTextComputed() {
			let loadingText = this.loadingText || "Loading..."
			if(this.isDeletingFile){
				loadingText = "Deleting..."
			}
			return loadingText
		},

		isCircleComputed(){
			return this.shape === "circle"
		},

		isStatusUploadError() {
			return this.fileUploadStatus === FileUploadStatus.ERROR
		},

		isStatusUploaded() {
			return this.fileUploadStatus === FileUploadStatus.COMPLETED
		},

		imageFilter() {
			return FileTypesEnum.IMAGES
		},

		previewActionsComputed() {
			return this.previewActions || DEFAULT_PREVIEW_ACTIONS_ARRAY
		},
	},
}
</script>

<style lang="scss">
.qw-uploader-root {
	//&.avatar-uploader .el-upload {
	//	border: 1px dashed #d9d9d9;
	//	border-radius: 6px;
	//	cursor: pointer;
	//	position: relative;
	//	overflow: hidden;
	//}
	//
	//&.avatar-uploader .el-upload:hover {
	//	border-color: #409EFF;
	//}
	//
	//.avatar-uploader-icon {
	//	font-size: 28px;
	//	color: #8c939d;
	//	width: 178px;
	//	height: 178px;
	//	line-height: 178px;
	//	text-align: center;
	//}
	//
	//.avatar {
	//	width: 178px;
	//	height: 178px;
	//	display: block;
	//}
	//
	.el-upload--picture-card, .el-upload-list__item {
		width: var(--uploader-size);
		height: var(--uploader-size);
	}

	.el-upload--picture-card{
		line-height: var(--uploader-size);
	}

	//
	//.el-upload-list__item-status-label {
	//	display: none !important;
	//}

	.preview-container {
		display: flex;
		flex: 1;
		align-items: center;
		justify-content: center;
		height: 100%;
		overflow: hidden;
	}

	.el-upload-list__item-thumbnail {
		object-fit: contain;
	}
}

</style>
